import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateTeam } from '@/api/teams';

interface CreateTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new team id once creation succeeds. */
  onCreated?: (teamId: string) => void;
}

export function CreateTeamDialog({ open, onOpenChange, onCreated }: CreateTeamDialogProps) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useCreateTeam({
    onSuccess: (team) => {
      show({ tone: 'success', title: 'Team created', description: team.team_name });
      onOpenChange(false);
      onCreated?.(team.id);
    },
    onError: () => show({ tone: 'danger', title: 'Could not create team' }),
  });

  function reset() {
    setName('');
    setDescription('');
    setError(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Team name is required.');
      return;
    }
    setError(null);
    create.mutate({ team_name: trimmed, team_description: description.trim() || undefined });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Create team</DialogTitle>
          <DialogDescription>
            Group people together, then invite members and share access.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="create-team-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'grid', gap: 16 }}
          >
            <Field label="Team name" required error={error ?? undefined}>
              {(props) => (
                <Input
                  {...props}
                  autoFocus
                  placeholder="Platform team"
                  value={name}
                  invalid={!!error}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </Field>
            <Field label="Description" description="Optional — what this team does.">
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  placeholder="Owns the edge platform and shared infrastructure…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-team-form"
            disabled={!name.trim() || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
